import React, { useState, useEffect, useMemo } from 'react';
import api from './api';
import { generatePlanningReport } from './utils/pdfGenerator';

const ProcessManager = () => {
    // --- Data State ---
    const [kapans, setKapans] = useState([]);          
    const [packets, setPackets] = useState([]);        
    const [selectedKapan, setSelectedKapan] = useState(null); 
    const [selectedPacket, setSelectedPacket] = useState(null); 

    // --- View State ---
    const [tab, setTab] = useState('Planning'); 
    const [showPlanDetails, setShowPlanDetails] = useState(false);
    
    // --- Import State ---
    const [importPreview, setImportPreview] = useState([]); 
    const [isProcessing, setIsProcessing] = useState(false);
    
    // --- INITIAL LOAD ---
    useEffect(() => {
        loadKapans();
    }, []);

    const loadKapans = async () => {
        try {
            const res = await api.get('/kapans');
            setKapans(res.data);
        } catch (err) { console.error("Load Stock Error", err); }
    };

    const handleSelectKapan = async (kapan) => {
        setSelectedKapan(kapan);
        setSelectedPacket(null);
        setImportPreview([]); // Clear previous uploads
        setIsProcessing(true);
        try {
            const res = await api.get(`/packets/${kapan._id}`);
            setPackets(res.data);
        } catch (err) { console.error(err); }
        finally { setIsProcessing(false); }
    };

    // --- SMART SCAN & VERIFY ---
    const handleFolderScan = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;
        
        if (!selectedKapan) return alert("Please select a Parent Kapan first!");

        setIsProcessing(true);
        const previewList = [];
        const parentName = selectedKapan.kapanName.trim(); // e.g., "E2"

        for (const file of files) {
            try {
                if (!file.name.toLowerCase().endsWith('.txt')) continue;
                
                const text = await readFileAsText(file);
                const planData = parseSarineString(text);
                
                if (planData && planData.stones.length > 0) {
                    // 1. Read the Internal ID from the file content (first column)
                    const firstLine = text.split('\n')[0];
                    const cols = firstLine.split('\\');
                    
                    const internalId = cols[0].trim(); // e.g. "E2-1" or "A5-1"
                    const roughWeight = parseFloat(cols[1] || 0);

                    // 2. VERIFICATION LOGIC
                    let status = "✅ OK";
                    let isMatch = true;
                    let warningColor = "text-success";

                    // Check: Does the Internal ID start with the Selected Parent Name?
                    // Example: If Parent is "E2", internalId must be "E2-..."
                    if (!internalId.startsWith(parentName)) {
                        status = `⚠️ Mismatch (File says: ${internalId})`;
                        isMatch = false;
                        warningColor = "text-danger fw-bold";
                    }

                    // Extract strict Packet Name (Suffix)
                    let packetName = internalId;
                    if (internalId.includes('-')) {
                        packetName = internalId.split('-').pop(); // Get "1" from "E2-1"
                    }

                    previewList.push({
                        id: file.name, 
                        fileName: file.name, 
                        internalId,       // The ID found inside the file
                        packetName,       // The suffix (1, 2, 3)
                        weight: roughWeight, 
                        stones: planData.stones.length, 
                        data: planData, 
                        
                        // Verification Results
                        status,
                        isMatch,
                        warningColor,
                        selected: isMatch // Only select if it matches
                    });
                }
            } catch (err) { console.warn("Skipping", file.name); }
        }
        setImportPreview(previewList);
        setIsProcessing(false);
    };

    // --- IMPORT ACTION ---
    const confirmImport = async () => {
        if (!selectedKapan) return alert("Select a Parent Kapan first!");
        
        // Only allow importing items that are SELECTED
        const selectedItems = importPreview.filter(i => i.selected);
        
        if (selectedItems.length === 0) return alert("No valid files selected.");
        
        // Double check: Block if user tries to force a mismatch
        const hasErrors = selectedItems.some(i => !i.isMatch);
        if (hasErrors) {
             if(!window.confirm("⚠️ WARNING: You are importing files that do NOT match the selected Parent Kapan.\n\nThis will create bad data. Are you sure?")) return;
        }

        setIsProcessing(true);
        try {
            const packetsPayload = selectedItems.map(item => ({
                name: item.packetName, // The suffix (e.g. "1")
                weight: item.weight, 
                planningData: item.data
            }));
            
            const res = await api.post('/packets/auto-create', { 
                parentKapanId: selectedKapan._id, 
                packets: packetsPayload 
            });
            
            alert(`✅ Success! Processed ${res.data.stats.n || selectedItems.length} packets.`);
            setImportPreview([]);
            handleSelectKapan(selectedKapan); 
        } catch (err) { alert("Error: " + err.message); }
        setIsProcessing(false);
    };

    // --- ACTIONS ---
    const moveToNext = async (packet, nextStatus) => {
        const note = prompt("Enter Note/Weight Return (Optional):", "");
        if (note === null) return;
        setIsProcessing(true);
        try {
            await api.put(`/packets/${packet._id}`, { 
                status: nextStatus, 
                historyNote: `Moved to ${nextStatus}. ${note}` 
            });
            alert("✅ Moved!");
            setSelectedPacket(null);
            handleSelectKapan(selectedKapan);
        } catch (err) { alert("Error moving packet."); }
        setIsProcessing(false);
    };

    const handleDeletePacket = async (packetId) => {
        if (!window.confirm("Delete this packet permanently?")) return;
        try {
            await api.delete(`/packets/${packetId}`);
            handleSelectKapan(selectedKapan);
        } catch (err) { alert("Error deleting."); }
    };

    const handleBulkDelete = async () => {
        if (!window.confirm(`⚠️ Delete ALL packets for ${selectedKapan.kapanName}?`)) return;
        setIsProcessing(true);
        try {
            await api.delete(`/packets/all/${selectedKapan._id}`);
            alert("✅ All packets deleted.");
            handleSelectKapan(selectedKapan);
        } catch (err) { alert("Error: " + err.message); }
        setIsProcessing(false);
    };

    // --- HELPERS ---
    const parseSarineString = (text) => {
        const lines = text.split('\n');
        const stones = [];
        let fileTotalVal = 0;
        lines.forEach((line) => {
            if (!line.includes('\\')) return; 
            const cols = line.split('\\');
            const stoneIdx = parseInt(cols[2]);
            if (!isNaN(stoneIdx) && stoneIdx > 0) {
                const rowTotalVal = parseFloat((cols[11] || "0").replace(/,/g, ''));
                if (rowTotalVal > fileTotalVal) fileTotalVal = rowTotalVal;
                stones.push({
                    stoneIndex: stoneIdx, weight: parseFloat(cols[17] || 0),   
                    shape: cols[15], diameter: parseFloat(cols[18] || 0),
                    color: cols[19], clarity: cols[20], value: parseFloat((cols[21] || "0").replace(/,/g, '')), 
                    label: cols[25]
                });
            }
        });
        if (stones.length === 0) return null;
        const calcTotal = stones.reduce((acc, s) => acc + (s.value || 0), 0);
        return { fileTotalValue: fileTotalVal, calculatedTotalValue: calcTotal, stones: stones.sort((a, b) => a.stoneIndex - b.stoneIndex) };
    };
    const readFileAsText = (file) => new Promise((r, j) => { const fr = new FileReader(); fr.onload=e=>r(e.target.result); fr.onerror=j; fr.readAsText(file); });
    
    // Toggle Checkbox
    const toggleImportItem = (fileName) => setImportPreview(prev => prev.map(i => i.fileName === fileName ? {...i, selected: !i.selected} : i));
    const toggleSelectAll = (val) => setImportPreview(prev => prev.map(i => i.isMatch ? {...i, selected: val} : i)); // Only verify match

    // Filter packets based on TAB
    const filteredPackets = useMemo(() => {
        if (!packets) return [];
        return packets.filter(p => {
            if (tab === 'Planning') return p.status === 'Planning';
            return p.status.includes(tab) || p.status === tab; 
        });
    }, [packets, tab]);

    return (
        <div className="container-fluid mt-4 mb-5 px-4">
            <h3 className="fw-bold text-primary mb-4">🏭 Factory Process Manager</h3>
            
            <div className="row">
                {/* --- LEFT SIDE: PARENT KAPANS --- */}
                <div className="col-md-3">
                    <div className="card shadow-sm h-100">
                        <div className="card-header bg-dark text-white fw-bold">1. Select Rough Stock</div>
                        <div className="card-body p-0 overflow-auto" style={{maxHeight: '80vh'}}>
                            {kapans.map(k => (
                                <div 
                                    key={k._id} 
                                    className={`p-3 border-bottom action-hover ${selectedKapan?._id === k._id ? 'bg-primary text-white' : ''}`}
                                    onClick={() => handleSelectKapan(k)}
                                    style={{cursor: 'pointer'}}
                                >
                                    <div className="d-flex justify-content-between align-items-center">
                                        <strong>{k.kapanName}</strong>
                                        <span className={`badge ${selectedKapan?._id === k._id ? 'bg-light text-primary' : 'bg-secondary'}`}>
                                            {k.weight} cts
                                        </span>
                                    </div>
                                    <small className={selectedKapan?._id === k._id ? 'text-white-50' : 'text-muted'}>
                                        Total Packets: {k.totalPackets || 0}
                                    </small>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* --- RIGHT SIDE: PACKETS & ACTIONS --- */}
                <div className="col-md-9">
                    {!selectedKapan ? (
                        <div className="alert alert-info text-center mt-5">
                            <h4>👈 Select a Rough Stone (Kapan) to begin</h4>
                        </div>
                    ) : (
                        <div>
                            {/* HEADER */}
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <div>
                                    <h4 className="fw-bold mb-0">Processing: {selectedKapan.kapanName}</h4>
                                    <small className="text-muted">ID: {selectedKapan._id}</small>
                                </div>
                                <button className="btn btn-danger btn-sm" onClick={handleBulkDelete}>🗑️ Delete All Packets</button>
                            </div>

                            {/* TABS */}
                            <ul className="nav nav-tabs mb-3">
                                {['Planning', 'Ready for Culet', 'Ready for Ghat', 'Ready for Polish', 'Polished'].map(t => (
                                    <li className="nav-item" key={t}>
                                        <button 
                                            className={`nav-link ${tab === t ? 'active fw-bold' : ''}`} 
                                            onClick={() => {setTab(t); setSelectedPacket(null);}}
                                        >
                                            {t}
                                        </button>
                                    </li>
                                ))}
                            </ul>

                            {/* --- IMPORT AREA --- */}
                            {tab === 'Planning' && (
                                <div className="card border-primary shadow-sm mb-4">
                                    <div className="card-header bg-light"><strong>📥 Import Sarine Files</strong></div>
                                    <div className="card-body">
                                        <input type="file" className="form-control mb-3" multiple webkitdirectory="" mozdirectory="" onChange={handleFolderScan} />
                                        
                                        {importPreview.length > 0 && (
                                            <div>
                                                <div className="table-responsive border rounded mb-2" style={{maxHeight: '250px'}}>
                                                    <table className="table table-sm table-hover small mb-0">
                                                        <thead className="table-light sticky-top">
                                                            <tr>
                                                                <th><input type="checkbox" onChange={(e)=>toggleSelectAll(e.target.checked)}/></th>
                                                                <th>File Name</th>
                                                                <th>Internal ID</th>
                                                                <th>Weight</th>
                                                                <th>Status</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {importPreview.map((item, i) => (
                                                                <tr key={i} className={!item.isMatch ? "table-danger" : ""}>
                                                                    <td><input type="checkbox" checked={item.selected} onChange={()=>toggleImportItem(item.fileName)} disabled={!item.isMatch}/></td>
                                                                    <td>{item.fileName}</td>
                                                                    <td><strong>{item.internalId}</strong></td>
                                                                    <td>{item.weight}</td>
                                                                    <td className={item.warningColor}>{item.status}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                                <button className="btn btn-success w-100 fw-bold" onClick={confirmImport} disabled={isProcessing}>
                                                    {isProcessing ? "Processing..." : "✅ Confirm Import"}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* --- PACKET LIST & DETAILS --- */}
                            <div className="row">
                                <div className="col-md-5">
                                    <div className="card shadow-sm">
                                        <div className="card-header bg-light fw-bold">List: {tab} ({filteredPackets.length})</div>
                                        <div className="card-body p-0 overflow-auto" style={{maxHeight: '50vh'}}>
                                            {filteredPackets.length === 0 ? <div className="p-3 text-muted text-center">No packets found.</div> : 
                                            <ul className="list-group list-group-flush">
                                                {filteredPackets.map(p => (
                                                    <li 
                                                        key={p._id} 
                                                        className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${selectedPacket?._id === p._id ? 'active' : ''}`}
                                                        onClick={() => setSelectedPacket(p)}
                                                        style={{cursor: 'pointer'}}
                                                    >
                                                        <div>
                                                            <strong>{p.fullPacketId}</strong>
                                                            <div className="small opacity-75">{p.status}</div>
                                                        </div>
                                                        <span className="badge bg-light text-dark border">{p.weight} ct</span>
                                                    </li>
                                                ))}
                                            </ul>}
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-7">
                                    {selectedPacket ? (
                                        <div className="card shadow border-success">
                                            <div className="card-header bg-success text-white d-flex justify-content-between">
                                                <span>Action: <strong>{selectedPacket.fullPacketId}</strong></span>
                                                <span>{selectedPacket.weight} ct</span>
                                            </div>
                                            <div className="card-body">
                                                {selectedPacket.planningData && (
                                                    <div className="mb-3">
                                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                                            <strong className="text-success">✅ Plan Available</strong>
                                                            <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowPlanDetails(!showPlanDetails)}>
                                                                {showPlanDetails ? "Hide Table" : "Show Stones"}
                                                            </button>
                                                        </div>
                                                        {showPlanDetails && (
                                                            <div className="table-responsive border rounded" style={{maxHeight: '200px'}}>
                                                                <table className="table table-sm table-striped small text-center mb-0">
                                                                    <thead><tr><th>#</th><th>Wt</th><th>Col</th><th>Cla</th><th>Val</th></tr></thead>
                                                                    <tbody>
                                                                        {selectedPacket.planningData.stones.map(s => (
                                                                            <tr key={s.stoneIndex}><td>{s.stoneIndex}</td><td>{s.weight}</td><td>{s.color}</td><td>{s.clarity}</td><td>{s.value}</td></tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                <div className="d-grid gap-2">
                                                    {tab === 'Planning' && <button className="btn btn-primary" onClick={() => moveToNext(selectedPacket, 'Ready for Culet')}>➡️ Send to Culet</button>}
                                                    {tab === 'Ready for Culet' && <button className="btn btn-primary" onClick={() => moveToNext(selectedPacket, 'Ready for Ghat')}>➡️ Send to Ghat</button>}
                                                    {tab === 'Ready for Ghat' && <button className="btn btn-primary" onClick={() => moveToNext(selectedPacket, 'Ready for Polish')}>➡️ Send to Polish</button>}
                                                    {tab === 'Ready for Polish' && <button className="btn btn-success" onClick={() => moveToNext(selectedPacket, 'Polished')}>💎 Mark Polished</button>}
                                                </div>
                                                <hr />
                                                <div className="text-end">
                                                    <button className="btn btn-outline-danger btn-sm" onClick={() => handleDeletePacket(selectedPacket._id)}>🗑️ Delete Packet</button>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="h-100 d-flex align-items-center justify-content-center text-muted border rounded bg-light p-4">
                                            Select a packet to perform actions.
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProcessManager;